import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BhabiPaginationProps {
  page: number;
  setPage: (page: number) => void;
  totalItems: number;
  pageSize: number;
  setPageSize?: (size: number) => void; // Optional for backward compatibility
  pageSizeOptions?: number[]; // Optional: custom page size options
}

export const BhabiPagination: React.FC<BhabiPaginationProps> = ({
  page,
  setPage,
  totalItems,
  pageSize,
  setPageSize,
  pageSizeOptions = [4, 6, 12, 24, 36],
}) => {
  const theme = useTheme();
  const totalPages = Math.ceil(totalItems / pageSize);

  const pageNumbers: (number | string)[] = [];
  pageNumbers.push(1);

  if (page > 3) pageNumbers.push('...');

  for (
    let i = Math.max(2, page - 1);
    i <= Math.min(totalPages - 1, page + 1);
    i++
  ) {
    if (i !== 1 && i !== totalPages) pageNumbers.push(i);
  }

  if (page < totalPages - 2) pageNumbers.push('...');

  if (totalPages > 1) pageNumbers.push(totalPages);

  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;

  const buttonBase = `inline-flex items-center justify-center min-w-11 h-11 px-2 rounded-full text-[0.85rem] font-bold transition-colors duration-150`;

  const inactiveStyle: React.CSSProperties = isDark
    ? {
        background: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        background: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
      };

  const activeStyle: React.CSSProperties = {
    background: theme.palette.mode === 'dark' ? primary : '#F5A524',
    color: '#1B1206',
    border: 'none',
    boxShadow: '0 6px 14px rgba(245, 165, 36, 0.3)',
  };

  const disabledStyle: React.CSSProperties = {
    background: isDark ? theme.palette.action.disabledBackground : theme.palette.grey[100],
    color: theme.palette.action.disabled,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'not-allowed',
    opacity: 0.5,
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Pagination Controls */}
      <div className="flex items-center gap-1 flex-wrap justify-center">
        {/* Previous Button */}
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className={`${buttonBase} ${page !== 1 ? 'hover:scale-105 active:scale-95' : ''}`}
          style={page === 1 ? disabledStyle : inactiveStyle}
          title="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Page Numbers */}
        {pageNumbers.map((num, idx) =>
          typeof num === 'number' ? (
            <button
              key={num}
              onClick={() => setPage(num)}
              className={`${buttonBase} ${page !== num ? 'hover:scale-105 active:scale-95' : ''}`}
              style={page === num ? activeStyle : inactiveStyle}
            >
              {num}
            </button>
          ) : (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 text-center text-sm font-bold opacity-50"
              style={{ color: theme.palette.text.secondary }}
            >
              …
            </span>
          )
        )}

        {/* Next Button */}
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className={`${buttonBase} ${page !== totalPages ? 'hover:scale-105 active:scale-95' : ''}`}
          style={page === totalPages ? disabledStyle : inactiveStyle}
          title="Next page"
        >
          <ChevronRight size={16} />
        </button>

        {/* Page Size Selector */}
        {/* {setPageSize && pageSizeOptions.length > 0 && (
          <select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            className={`h-9 px-2 rounded-lg text-[0.65rem] font-bold uppercase tracking-widest border-2 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 ${
              theme.palette.mode === 'dark'
                ? 'bg-gray-800 text-white border-gray-700 hover:border-gray-600'
                : 'bg-gray-100 text-gray-800 border-gray-300 hover:border-gray-400'
            }`}
          >
            {pageSizeOptions.map(opt => (
              <option key={opt} value={opt}>
                {opt === 6 || opt === 4 ? `0${opt}` : opt}
              </option>
            ))}
          </select>
        )} */}
      </div>
    </div>
  );
};

interface InitialPageSize {
  readonly mobile: number;
  readonly desktop: number;
}

// Overload 1 – legacy: fixed mobile/desktop page sizes
export function usePaginationHelper<T>(
  initialPageSize: InitialPageSize,
  data?: T[]
): {
  page: number;
  setPage: (p: number) => void;
  paginatedData: T[];
  setPageSize: (s: number) => void;
  pageSize: number;
  initialPageSizeValue: number;
};

// Overload 2 – viewport-aware: calculates page size from available height
export function usePaginationHelper<T>(
  chromePx: number,
  rowHeight: number,
  data?: T[]
): {
  page: number;
  setPage: (p: number) => void;
  paginatedData: T[];
  setPageSize: (s: number) => void;
  pageSize: number;
  initialPageSizeValue: number;
};

export function usePaginationHelper<T>(
  pageSizeOrChrome: InitialPageSize | number,
  dataOrRowHeight?: T[] | number,
  data?: T[]
) {
  const [page, setPage] = React.useState(1);
  const viewportHeight = useDeviceHeight();

  // Resolve page size depending on which overload was used
  const resolvedPageSize = React.useMemo(() => {
    if (typeof pageSizeOrChrome === 'number') {
      // Overload 2: viewport-aware
      const chromePx = pageSizeOrChrome;
      const rowHeight = dataOrRowHeight as number;
      return Math.max(5, Math.floor((viewportHeight - chromePx) / rowHeight));
    }
    // Overload 1: fixed mobile/desktop – use the same threshold as pre-623c3d9.
    const isMobile = viewportHeight < 700;
    return isMobile ? pageSizeOrChrome.mobile : pageSizeOrChrome.desktop;
  }, [pageSizeOrChrome, dataOrRowHeight, viewportHeight]);

  const [pageSize, setPageSize] = React.useState(resolvedPageSize);

  // Keep pageSize in sync when viewport changes (overload 2 only)
  React.useEffect(() => {
    if (typeof pageSizeOrChrome === 'number') {
      setPageSize(resolvedPageSize);
    }
  }, [resolvedPageSize, pageSizeOrChrome]);

  // Resolve data depending on which overload was used
  const resolvedData: T[] | undefined =
    typeof pageSizeOrChrome === 'number'
      ? (data as T[] | undefined)
      : (dataOrRowHeight as T[] | undefined);

  const paginatedData = useMemo((): T[] => {
    if (!resolvedData?.length) return [];
    const start = (page - 1) * pageSize;
    return resolvedData.slice(start, start + pageSize);
  }, [resolvedData, page, pageSize]);

  React.useEffect(() => {
    setPage(1);
  }, [resolvedData]);

  return {
    page,
    setPage,
    paginatedData,
    setPageSize,
    pageSize,
    initialPageSizeValue: resolvedPageSize,
  };
}

/** Vertical safe-area insets (notch + home indicator) in px; 0 on the web. */
const readSafeInsetsY = () => {
  if (typeof document === 'undefined' || !document.body) return 0;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding-top:env(safe-area-inset-top,0px);' +
    'padding-bottom:env(safe-area-inset-bottom,0px);';
  document.body.appendChild(probe);
  const cs = window.getComputedStyle(probe);
  const total =
    (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  probe.remove();
  return total;
};

// Height actually available to page content: the visible viewport minus the
// notch / home-indicator insets the bounded screens reserve.
const getVisibleHeight = () =>
  (window.visualViewport?.height ?? window.innerHeight) - readSafeInsetsY();

const useDeviceHeight = () => {
  const [height, setHeight] = React.useState(getVisibleHeight);

  React.useEffect(() => {
    const handleResize = () => setHeight(getVisibleHeight());
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleResize);
      return () => vv.removeEventListener('resize', handleResize);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
};
